import { useState, useRef, useEffect } from "react";
import { Smile, Send, Paperclip, Loader2 } from "lucide-react";
import EmojiPicker from "emoji-picker-react";
import { chatBulkUploadApi } from "../../app/api";
import toast from "react-hot-toast";
import imageCompression from "browser-image-compression";
 
export default function ChatInput({ onSend, members = [] }) {
  const [text, setText] = useState("");
  const [showEmoji, setShowEmoji] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isCompressing, setIsCompressing] = useState(false);
 
  // Compression Modal State
  const [showCompressionModal, setShowCompressionModal] = useState(false);
  const [pendingFiles, setPendingFiles] = useState([]);
 
  // Tagging State
  const [showTagList, setShowTagList] = useState(false);
  const [tagSearch, setTagSearch] = useState("");
  const [cursorPos, setCursorPos] = useState(0);
 
  const fileInputRef = useRef(null);
  const emojiRef = useRef(null);
  const inputRef = useRef(null);
 
  const handleSend = () => {
    if (!text.trim()) return;
 
    onSend(text);
    setText("");
    setShowEmoji(false);
    setShowTagList(false);
  };
 
  const handleChange = (e) => {
    const val = e.target.value;
    const pos = e.target.selectionStart;
    setText(val);
    setCursorPos(pos);
 
    // Detect @ mention
    const textBeforeCursor = val.slice(0, pos);
    const words = textBeforeCursor.split(/\s/);
    const lastWord = words[words.length - 1];
 
    if (lastWord.startsWith("@")) {
      setShowTagList(true);
      setTagSearch(lastWord.slice(1).toLowerCase());
    } else {
      setShowTagList(false);
    }
  };
 
  const handleSelectTag = (member) => {
    const textBeforeCursor = text.slice(0, cursorPos);
    const textAfterCursor = text.slice(cursorPos);
   
    const words = textBeforeCursor.split(/\s/);
    words[words.length - 1] = `@${member.first_name}${member.last_name ? ' ' + member.last_name : ''} `;
   
    const newText = words.join(" ") + textAfterCursor;
    setText(newText);
    setShowTagList(false);
    inputRef.current?.focus();
  };
 
  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };
 
  const handleFiles = async (files) => {
    if (!files || files.length === 0) return;
   
    if (files.length > 10) {
      toast.error("You can only upload up to 10 files at once.");
      return;
    }
 
    const MAX_SIZE = 50 * 1024 * 1024; // 50MB
    const COMPRESSION_THRESHOLD = 5 * 1024 * 1024; // 5MB
 
    const oversized = Array.from(files).filter(f => f.size > MAX_SIZE);
    if (oversized.length > 0) {
      toast.error(`Some files are too large. Max size is 50MB. (${oversized[0].name})`);
      return;
    }
 
    const needsCompression = Array.from(files).filter(f => f.size > COMPRESSION_THRESHOLD && f.type.startsWith("image/"));
   
    if (needsCompression.length > 0) {
      setPendingFiles(Array.from(files));
      setShowCompressionModal(true);
      return;
    }
 
    await performUpload(Array.from(files));
  };
 
  const performUpload = async (filesToUpload) => {
    try {
      setIsUploading(true);
      const formData = new FormData();
      filesToUpload.forEach(file => {
        formData.append("documents", file);
      });
 
      const res = await chatBulkUploadApi(formData);
     
      if (res?.success && res.files) {
        res.files.forEach(file => {
          const type = file.mimetype?.startsWith("image/") ? "image" : "file";
          onSend(file.url, type);
        });
      }
    } catch (err) {
      console.error("Upload error:", err);
      toast.error("Failed to upload files.");
    } finally {
      setIsUploading(false);
    }
  };
 
  const handleStartCompression = async () => {
    setIsCompressing(true);
    try {
      const compressedFiles = await Promise.all(
        pendingFiles.map(async (file) => {
          if (file.type.startsWith("image/") && file.size > 5 * 1024 * 1024) {
            const options = {
              maxSizeMB: 1,
              maxWidthOrHeight: 1920,
              useWebWorker: true,
            };
            try {
              const compressedBlob = await imageCompression(file, options);
              return new File([compressedBlob], file.name, { type: file.type });
            } catch (e) {
              console.error("Compression failed for", file.name, e);
              return file;
            }
          }
          return file;
        })
      );
 
      setShowCompressionModal(false);
      await performUpload(compressedFiles);
    } catch (err) {
      toast.error("Error during compression");
    } finally {
      setIsCompressing(false);
      setPendingFiles([]);
    }
  };
 
  const handlePaste = (e) => {
    const items = e.clipboardData.items;
    const files = [];
    for (let i = 0; i < items.length; i++) {
      if (items[i].kind === "file") {
        files.push(items[i].getAsFile());
      }
    }
    if (files.length > 0) {
      handleFiles(files);
    }
  };
 
  const onEmojiClick = (emojiObject) => {
    setText((prev) => prev + emojiObject.emoji);
  };
 
  // Close emoji picker when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (emojiRef.current && !emojiRef.current.contains(event.target)) {
        setShowEmoji(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);
 
  return (
    <div className="relative p-3 border-t border-white/10 flex items-center gap-3">
      <div className="flex-1 flex gap-2 items-center bg-white/10 backdrop-blur rounded-xl px-3 py-2 relative" ref={emojiRef}>
       
        <button
          onClick={() => setShowEmoji((prev) => !prev)}
          className="opacity-70 hover:opacity-100 text-white p-1"
          type="button"
        >
          <Smile size={20} />
        </button>
 
        <button
          onClick={() => fileInputRef.current?.click()}
          className="opacity-70 hover:opacity-100 text-white p-1"
          type="button"
          disabled={isUploading}
        >
          {isUploading ? <Loader2 size={18} className="animate-spin" /> : <Paperclip size={18} />}
        </button>
 
        <input
          type="file"
          multiple
          className="hidden"
          ref={fileInputRef}
          onChange={(e) => handleFiles(e.target.files)}
        />
 
        {showEmoji && (
          <div className="absolute bottom-full left-0 mb-4 z-50 animate-in zoom-in-95 slide-in-from-bottom-10 duration-200">
            <div className="relative group">
              <div className="absolute -inset-1 bg-gradient-to-tr from-indigo-500 to-purple-600 rounded-[2rem] blur opacity-20 group-hover:opacity-40 transition duration-1000"></div>
             
              <div className="relative bg-[#0f111a]/95 backdrop-blur-3xl border border-white/10 rounded-[1.5rem] shadow-[0_20px_50px_rgba(0,0,0,0.5)] overflow-hidden">
                <EmojiPicker
                  onEmojiClick={onEmojiClick}
                  theme="dark"
                  searchDisabled={false}
                  skinTonesDisabled={true}
                  autoFocusSearch={false}
                  width={320}
                  height={400}
                  previewConfig={{ showPreview: false }}
                  className="!bg-transparent !border-none"
                />
              </div>
            </div>
          </div>
        )}
 
        <input
          ref={inputRef}
          value={text}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          className="flex-1 bg-transparent outline-none text-sm text-white"
          placeholder="Type a message..."
          disabled={isUploading}
        />
 
        {showTagList && members.length > 0 && (
          <div className="absolute bottom-full left-10 mb-2 w-64 bg-[#1a1c23]/95 backdrop-blur-xl border border-white/10 rounded-xl shadow-2xl overflow-hidden z-[60] animate-in slide-in-from-bottom-2">
            <div className="p-2 border-b border-white/5 bg-white/5">
              <span className="text-[10px] text-white/40 font-bold uppercase tracking-wider">Mention Someone</span>
            </div>
            <div className="max-h-48 overflow-y-auto custom-scrollbar">
              {members
                .filter(m =>
                  `${m.first_name} ${m.last_name || ""}`.toLowerCase().includes(tagSearch)
                )
                .map((member, idx) => (
                  <button
                    key={idx}
                    onClick={() => handleSelectTag(member)}
                    className="w-full text-left px-3 py-2 hover:bg-white/10 transition-colors flex items-center gap-3"
                    type="button"
                  >
                    <div className="w-6 h-6 rounded-full bg-indigo-500/20 flex items-center justify-center text-[10px] font-bold text-indigo-400 border border-indigo-500/30">
                      {member.first_name[0]}
                    </div>
                    <div>
                      <p className="text-sm font-medium text-white/90">{member.first_name} {member.last_name || ""}</p>
                      <p className="text-[10px] text-white/40">@{member.first_name.toLowerCase()}</p>
                    </div>
                  </button>
                ))}
            </div>
          </div>
        )}
 
        <button
          onClick={handleSend}
          className="text-purple-400 hover:text-purple-300 transition-colors p-1 disabled:opacity-50"
          disabled={isUploading || !text.trim()}
        >
          <Send size={20} />
        </button>
      </div>
 
      {/* COMPRESSION MODAL */}
      {showCompressionModal && (
        <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4 animate-in fade-in duration-200">
          <div className="bg-[#1a1c23] border border-white/10 rounded-[2rem] p-8 max-w-sm w-full shadow-[0_20px_50px_rgba(0,0,0,0.5)] text-center relative overflow-hidden group">
            <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-indigo-500 via-purple-500 to-indigo-500 animate-gradient-x" />
           
            <div className="w-20 h-20 bg-indigo-500/10 rounded-full flex items-center justify-center mx-auto mb-6 border border-indigo-500/20 group-hover:scale-110 transition-transform duration-500">
              <Paperclip className="text-indigo-400 animate-pulse" size={32} />
            </div>
 
            <h3 className="text-xl font-bold text-white mb-2">Files are too large</h3>
            <p className="text-sm text-white/50 mb-8 leading-relaxed">
              Some images are over 5MB. Would you like to compress them for a faster and smoother upload?
            </p>
 
            <div className="flex flex-col gap-3">
              <button
                onClick={handleStartCompression}
                disabled={isCompressing}
                className="w-full bg-indigo-600 hover:bg-indigo-500 text-white font-bold py-3.5 rounded-2xl transition-all active:scale-95 flex items-center justify-center gap-2 shadow-lg shadow-indigo-600/20 disabled:opacity-50"
              >
                {isCompressing ? (
                  <>
                    <Loader2 size={18} className="animate-spin" />
                    Compressing...
                  </>
                ) : (
                  "Compress & Upload"
                )}
              </button>
             
              <button
                onClick={() => {
                  setShowCompressionModal(false);
                  setPendingFiles([]);
                }}
                disabled={isCompressing}
                className="w-full bg-white/5 hover:bg-white/10 text-white/70 font-bold py-3.5 rounded-2xl transition-all active:scale-95 disabled:opacity-50"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
 

// import { useState, useRef, useEffect } from "react";
// import { Smile, Send, Paperclip, Loader2 } from "lucide-react";
// import EmojiPicker from "emoji-picker-react";
// import { chatBulkUploadApi } from "../../app/api";
// import toast from "react-hot-toast";
// import imageCompression from "browser-image-compression";

// export default function ChatInput({ onSend, members = [] }) {
//   const [text, setText] = useState("");
//   const [showEmoji, setShowEmoji] = useState(false);
//   const [isUploading, setIsUploading] = useState(false);
//   const [isCompressing, setIsCompressing] = useState(false);
  
//   // Compression Modal State
//   const [showCompressionModal, setShowCompressionModal] = useState(false);
//   const [pendingFiles, setPendingFiles] = useState([]);
  
//   // Tagging State
//   const [showTagList, setShowTagList] = useState(false);
//   const [tagSearch, setTagSearch] = useState("");
//   const [cursorPos, setCursorPos] = useState(0);

//   const fileInputRef = useRef(null);
//   const emojiRef = useRef(null);
//   const inputRef = useRef(null);

//   const handleSend = () => {
//     if (!text.trim()) return;

//     onSend(text);
//     setText("");
//     setShowEmoji(false);
//     setShowTagList(false);
//   };

//   const handleChange = (e) => {
//     const val = e.target.value;
//     const pos = e.target.selectionStart;
//     setText(val);
//     setCursorPos(pos);

//     // Detect @ mention
//     const textBeforeCursor = val.slice(0, pos);
//     const words = textBeforeCursor.split(/\s/);
//     const lastWord = words[words.length - 1];

//     if (lastWord.startsWith("@")) {
//       setShowTagList(true);
//       setTagSearch(lastWord.slice(1).toLowerCase());
//     } else {
//       setShowTagList(false);
//     }
//   };

//   const handleSelectTag = (member) => {
//     const textBeforeCursor = text.slice(0, cursorPos);
//     const textAfterCursor = text.slice(cursorPos);
    
//     const words = textBeforeCursor.split(/\s/);
//     words[words.length - 1] = `@${member.first_name}${member.last_name ? ' ' + member.last_name : ''} `;
    
//     const newText = words.join(" ") + textAfterCursor;
//     setText(newText);
//     setShowTagList(false);
//     inputRef.current?.focus();
//   };

//   const handleKeyDown = (e) => {
//     if (e.key === "Enter" && !e.shiftKey) {
//       e.preventDefault();
//       handleSend();
//     }
//   };

//   const handleFiles = async (files) => {
//     if (!files || files.length === 0) return;
    
//     if (files.length > 10) {
//       toast.error("You can only upload up to 10 files at once.");
//       return;
//     }

//     const MAX_SIZE = 50 * 1024 * 1024; // 50MB
//     const COMPRESSION_THRESHOLD = 5 * 1024 * 1024; // 5MB

//     const oversized = Array.from(files).filter(f => f.size > MAX_SIZE);
//     if (oversized.length > 0) {
//       toast.error(`Some files are too large. Max size is 50MB. (${oversized[0].name})`);
//       return;
//     }

//     const needsCompression = Array.from(files).filter(f => f.size > COMPRESSION_THRESHOLD && f.type.startsWith("image/"));
    
//     if (needsCompression.length > 0) {
//       setPendingFiles(Array.from(files));
//       setShowCompressionModal(true);
//       return;
//     }

//     await performUpload(Array.from(files));
//   };

//   const performUpload = async (filesToUpload) => {
//     try {
//       setIsUploading(true);
//       const formData = new FormData();
//       filesToUpload.forEach(file => {
//         formData.append("documents", file);
//       });

//       const res = await chatBulkUploadApi(formData);
      
//       if (res?.success && res.files) {
//         res.files.forEach(file => {
//           const type = file.mimetype?.startsWith("image/") ? "image" : "file";
//           onSend(file.url, type);
//         });
//       }
//     } catch (err) {
//       console.error("Upload error:", err);
//       toast.error("Failed to upload files.");
//     } finally {
//       setIsUploading(false);
//     }
//   };

//   const handleStartCompression = async () => {
//     setIsCompressing(true);
//     try {
//       const compressedFiles = await Promise.all(
//         pendingFiles.map(async (file) => {
//           if (file.type.startsWith("image/") && file.size > 5 * 1024 * 1024) {
//             const options = {
//               maxSizeMB: 1,
//               maxWidthOrHeight: 1920,
//               useWebWorker: true,
//             };
//             try {
//               const compressedBlob = await imageCompression(file, options);
//               return new File([compressedBlob], file.name, { type: file.type });
//             } catch (e) {
//               console.error("Compression failed for", file.name, e);
//               return file;
//             }
//           }
//           return file;
//         })
//       );

//       setShowCompressionModal(false);
//       await performUpload(compressedFiles);
//     } catch (err) {
//       toast.error("Error during compression");
//     } finally {
//       setIsCompressing(false);
//       setPendingFiles([]);
//     }
//   };

//   const handlePaste = (e) => {
//     const items = e.clipboardData.items;
//     const files = [];
//     for (let i = 0; i < items.length; i++) {
//       if (items[i].kind === "file") {
//         files.push(items[i].getAsFile());
//       }
//     }
//     if (files.length > 0) {
//       handleFiles(files);
//     }
//   };

//   const onEmojiClick = (emojiObject) => {
//     setText((prev) => prev + emojiObject.emoji);
//   };

//   // Close emoji picker when clicking outside
//   useEffect(() => {
//     const handleClickOutside = (event) => {
//       if (emojiRef.current && !emojiRef.current.contains(event.target)) {
//         setShowEmoji(false);
//       }
//     };
//     document.addEventListener("mousedown", handleClickOutside);
//     return () => {
//       document.removeEventListener("mousedown", handleClickOutside);
//     };
//   }, []);

//   return (
//     <div className="relative p-3 border-t border-white/10 flex items-center gap-3">
//       <div className="flex-1 flex gap-2 items-center bg-white/10 backdrop-blur rounded-xl px-3 py-2 relative" ref={emojiRef}>
        
//         <button
//           onClick={() => setShowEmoji((prev) => !prev)}
//           className="opacity-70 hover:opacity-100 text-white p-1"
//           type="button"
//         >
//           <Smile size={20} />
//         </button>

//         <button
//           onClick={() => fileInputRef.current?.click()}
//           className="opacity-70 hover:opacity-100 text-white p-1"
//           type="button"
//           disabled={isUploading}
//         >
//           {isUploading ? <Loader2 size={18} className="animate-spin" /> : <Paperclip size={18} />}
//         </button>

//         <input 
//           type="file"
//           multiple
//           className="hidden"
//           ref={fileInputRef}
//           onChange={(e) => handleFiles(e.target.files)}
//         />

//         {showEmoji && (
//           <div className="absolute bottom-full left-0 mb-4 z-50 animate-in zoom-in-95 slide-in-from-bottom-10 duration-200">
//             <div className="relative group">
//               <div className="absolute -inset-1 bg-gradient-to-tr from-indigo-500 to-purple-600 rounded-[2rem] blur opacity-20 group-hover:opacity-40 transition duration-1000"></div>
              
//               <div className="relative bg-[#0f111a]/95 backdrop-blur-3xl border border-white/10 rounded-[1.5rem] shadow-[0_20px_50px_rgba(0,0,0,0.5)] overflow-hidden">
//                 <EmojiPicker
//                   onEmojiClick={onEmojiClick}
//                   theme="dark"
//                   searchDisabled={false}
//                   skinTonesDisabled={true}
//                   autoFocusSearch={false}
//                   width={320}
//                   height={400}
//                   previewConfig={{ showPreview: false }}
//                   className="!bg-transparent !border-none"
//                 />
//               </div>
//             </div>
//           </div>
//         )}

//         <input
//           ref={inputRef}
//           value={text}
//           onChange={handleChange}
//           onKeyDown={handleKeyDown}
//           onPaste={handlePaste}
//           className="flex-1 bg-transparent outline-none text-sm text-white"
//           placeholder="Type a message..."
//           disabled={isUploading}
//         />

//         {showTagList && members.length > 0 && (
//           <div className="absolute bottom-full left-10 mb-2 w-64 bg-[#1a1c23]/95 backdrop-blur-xl border border-white/10 rounded-xl shadow-2xl overflow-hidden z-[60] animate-in slide-in-from-bottom-2">
//             <div className="p-2 border-b border-white/5 bg-white/5">
//               <span className="text-[10px] text-white/40 font-bold uppercase tracking-wider">Mention Someone</span>
//             </div>
//             <div className="max-h-48 overflow-y-auto custom-scrollbar">
//               {members
//                 .filter(m => 
//                   `${m.first_name} ${m.last_name || ""}`.toLowerCase().includes(tagSearch)
//                 )
//                 .map((member, idx) => (
//                   <button
//                     key={idx}
//                     onClick={() => handleSelectTag(member)}
//                     className="w-full text-left px-3 py-2 hover:bg-white/10 transition-colors flex items-center gap-3"
//                     type="button"
//                   >
//                     <div className="w-6 h-6 rounded-full bg-indigo-500/20 flex items-center justify-center text-[10px] font-bold text-indigo-400 border border-indigo-500/30">
//                       {member.first_name[0]}
//                     </div>
//                     <div>
//                       <p className="text-sm font-medium text-white/90">{member.first_name} {member.last_name || ""}</p>
//                       <p className="text-[10px] text-white/40">@{member.first_name.toLowerCase()}</p>
//                     </div>
//                   </button>
//                 ))}
//             </div>
//           </div>
//         )}

//         <button 
//           onClick={handleSend}
//           className="text-purple-400 hover:text-purple-300 transition-colors p-1 disabled:opacity-50"
//           disabled={isUploading || !text.trim()}
//         >
//           <Send size={20} />
//         </button>
//       </div>

//       {/* COMPRESSION MODAL */}
//       {showCompressionModal && (
//         <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4 animate-in fade-in duration-200">
//           <div className="bg-[#1a1c23] border border-white/10 rounded-[2rem] p-8 max-w-sm w-full shadow-[0_20px_50px_rgba(0,0,0,0.5)] text-center relative overflow-hidden group">
//             <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-indigo-500 via-purple-500 to-indigo-500 animate-gradient-x" />
            
//             <div className="w-20 h-20 bg-indigo-500/10 rounded-full flex items-center justify-center mx-auto mb-6 border border-indigo-500/20 group-hover:scale-110 transition-transform duration-500">
//               <Paperclip className="text-indigo-400 animate-pulse" size={32} />
//             </div>

//             <h3 className="text-xl font-bold text-white mb-2">Files are too large</h3>
//             <p className="text-sm text-white/50 mb-8 leading-relaxed">
//               Some images are over 5MB. Would you like to compress them for a faster and smoother upload?
//             </p>

//             <div className="flex flex-col gap-3">
//               <button
//                 onClick={handleStartCompression}
//                 disabled={isCompressing}
//                 className="w-full bg-indigo-600 hover:bg-indigo-500 text-white font-bold py-3.5 rounded-2xl transition-all active:scale-95 flex items-center justify-center gap-2 shadow-lg shadow-indigo-600/20 disabled:opacity-50"
//               >
//                 {isCompressing ? (
//                   <>
//                     <Loader2 size={18} className="animate-spin" />
//                     Compressing...
//                   </>
//                 ) : (
//                   "Compress & Upload"
//                 )}
//               </button>
              
//               <button
//                 onClick={() => {
//                   setShowCompressionModal(false);
//                   setPendingFiles([]);
//                 }}
//                 disabled={isCompressing}
//                 className="w-full bg-white/5 hover:bg-white/10 text-white/70 font-bold py-3.5 rounded-2xl transition-all active:scale-95 disabled:opacity-50"
//               >
//                 Cancel
//               </button>
//             </div>
//           </div>
//         </div>
//       )}
//     </div>
//   );
// }